import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTenant } from '../../../../contexts/TenantContext';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { usePreferences } from '../../../../hooks/usePreferences';
import { useListingField } from '@brightmls/nextgen.lae.flc';
import { FeatureFlags } from '../../../../flags';
import { Permissions } from '../../../../system/permission';
import { PreferenceKey } from '../../../../hooks/usePreferences';
import {
  MediaCenterConfig,
  MediaType,
  ResourceFields,
} from '@brightmls/nextgen.lae.mediacenter';
import { MEDIA_CONSTRAINTS } from './mediaConstraints';

export function useMediaCenterConfig(
  listingKey: string,
  resource: ResourceFields,
): MediaCenterConfig {
  const { allowed } = useAuth();
  const { supportsClientResoWebApi, getConfigValue, isAddEditHost, tenant } =
    useTenant();
  const { hasFeature } = useFeatureFlags();
  const { usePreference } = usePreferences();
  const [searchParams] = useSearchParams();

  const prefix = isAddEditHost() ? `/${tenant}` : '';

  const isResoSystem = supportsClientResoWebApi();
  const isDebugMode = searchParams.has('debug');

  // Permissions
  const permissions = {
    photos: {
      add: allowed({ permission: Permissions.UPLOAD_LISTING_PHOTOS }),
      edit: allowed({ permission: Permissions.UPLOAD_LISTING_PHOTOS }),
      delete: allowed({ permission: Permissions.UPLOAD_LISTING_PHOTOS }),
      reorder: allowed({ permission: Permissions.UPLOAD_LISTING_PHOTOS }),
    },
    documents: {
      add: allowed({ permission: Permissions.UPLOAD_DOCUMENTS }),
      edit: allowed({ permission: Permissions.UPLOAD_DOCUMENTS }),
      delete: allowed({ permission: Permissions.UPLOAD_DOCUMENTS }),
      reorder: allowed({ permission: Permissions.UPLOAD_DOCUMENTS }),
    },
    tours: {
      add: allowed({ permission: Permissions.ADD_EDIT_VIRTUAL_TOURS }),
      edit: allowed({ permission: Permissions.ADD_EDIT_VIRTUAL_TOURS }),
      delete: allowed({ permission: Permissions.ADD_EDIT_VIRTUAL_TOURS }),
      reorder: allowed({ permission: Permissions.ADD_EDIT_VIRTUAL_TOURS }),
    },
  };

  // Feature flags
  const features = {
    aiDescriptions: hasFeature(FeatureFlags.NGW_AI_IMAGE_DESCRIPTIONS),
    aiDescriptionsOptIn: hasFeature(FeatureFlags.NGW_AI_IMAGE_DESCRIPTIONS),
    cloudUploaders: hasFeature(FeatureFlags.NGW_MEDIA_CLOUD_UPLOADS),
    objectDetection: hasFeature(FeatureFlags.NGW_OBJECT_DETECTION),
    photoEditor: hasFeature(FeatureFlags.NGW_EDIT_IMAGE),
    bulkEdit: hasFeature(FeatureFlags.NGW_MULTI_DRAG_DROP),
    termsOfService: hasFeature(FeatureFlags.NGW_USER_TERMS),
    autoHideOffMarket: hasFeature(FeatureFlags.NGW_HIDE_MEDIA_OFF_MARKET),
    documentFloorPlans: isResoSystem,
    documentVisibility: isResoSystem,
    documentTitleDescription: isResoSystem,
  };

  // Preferences
  const [showDescriptions, setShowDescriptions] = usePreference<boolean>(
    PreferenceKey.SHOW_DESCRIPTIONS,
    true,
  );
  const [aiDescriptionsEnabled, setAIDescriptionsEnabled] =
    usePreference<boolean>(PreferenceKey.AI_DESCRIPTIONS_ENABLED, true);

  // Cloud providers
  const cloudProviders = features.cloudUploaders
    ? {
        googleDrive: hasFeature(FeatureFlags.NGW_MEDIA_CLOUD_PROVIDER_GOOGLE)
          ? {
              clientId: getConfigValue(
                'MEDIA_PROVIDER_GOOGLE_DRIVE_CLIENT_ID',
                '',
              ),
              apiKey: getConfigValue('MEDIA_PROVIDER_GOOGLE_DRIVE_API_KEY', ''),
              appId: getConfigValue('MEDIA_PROVIDER_GOOGLE_DRIVE_APP_ID', ''),
            }
          : undefined,
        dropbox: hasFeature(FeatureFlags.NGW_MEDIA_CLOUD_PROVIDER_DROPBOX)
          ? {
              appId: getConfigValue('MEDIA_PROVIDER_DROPBOX_APP_KEY', ''),
            }
          : undefined,
        oneDrive: hasFeature(FeatureFlags.NGW_MEDIA_CLOUD_PROVIDER_ONEDRIVE)
          ? {
              appId: getConfigValue('MEDIA_PROVIDER_ONEDRIVE_APP_ID', ''),
            }
          : undefined,
      }
    : undefined;

  return useMemo(
    () => ({
      enabledTypes: MEDIA_CONSTRAINTS.getEnabledTypes(isResoSystem),
      defaultType: MediaType.Photo,
      categoryMapping: MEDIA_CONSTRAINTS.getCategoryMapping(isResoSystem),
      routing: {
        basePath: `${prefix}/edit/${listingKey}/media`,
        paths: {
          photos: 'photos',
          documents: 'documents',
          tours: 'tours',
        },
      },
      resource,
      features,
      preferences: {
        showDescriptions: [showDescriptions, setShowDescriptions],
        aiDescriptionsEnabled: [
          aiDescriptionsEnabled,
          setAIDescriptionsEnabled,
        ],
      },
      limits: MEDIA_CONSTRAINTS.limits,
      permissions,
      cloudProviders,
      descriptionLimits: {
        maxShortLength: 50,
        maxLongLength: 4000,
      },
      debug: isDebugMode,
    }),
    [
      isDebugMode,
      listingKey,
      isResoSystem,
      resource,
      features.aiDescriptions,
      features.aiDescriptionsOptIn,
      features.cloudUploaders,
      features.objectDetection,
      features.photoEditor,
      features.bulkEdit,
      features.termsOfService,
      features.autoHideOffMarket,
      permissions.photos.add,
      permissions.documents.add,
      permissions.tours.add,
      cloudProviders,
      showDescriptions,
      setShowDescriptions,
      aiDescriptionsEnabled,
      setAIDescriptionsEnabled,
    ],
  );
}
